/**
 * Hook do próximo 6du com fallback gracioso (Task #61).
 *
 * Estratégia:
 * 1. Tenta /api/calendario/proximo-6du via React Query–style cache
 *    (module-level Map + TTL 24h — sem dep nova). Resposta:
 *      { data: Date, mes_referencia, is_estimativa, fonte }
 * 2. Em caso de falha (rede/auth/5xx) cai pra heurística OFFLINE local
 *    (`_calcularProximo6duHeuristica` — alias intencional de
 *    `calcularProximo6DU`, marca uso de fallback consciente sem warning
 *    de @deprecated) com is_estimativa=true / fonte="heuristica_6du".
 *    Mesma forma que o backend devolveria pra ano não importado.
 *
 * Cache module-level porque (a) a resposta muda no máximo 1×/dia (passou
 * o 6du do mês), (b) evita N requests entre o SixDuCard e KPIs derivados,
 * (c) não justifica adicionar @tanstack/react-query no bundle só por isso.
 */
import { useEffect, useState } from "react";
import { getProximo6du } from "../api/client";
import type { FonteCalendario6du } from "../api/types";
import { _calcularProximo6duHeuristica } from "../utils/dateBr";

export interface Proximo6duInfo {
  data: Date;
  mes_referencia: string; // YYYY-MM
  is_estimativa: boolean;
  fonte: FonteCalendario6du;
}

const TTL_MS = 24 * 60 * 60 * 1000; // 24h

interface CacheEntry {
  expiresAt: number;
  promise: Promise<Proximo6duInfo>;
}

const cache = new Map<string, CacheEntry>();

function heuristicaInfo(hoje: Date): Proximo6duInfo {
  const data = _calcularProximo6duHeuristica(hoje);
  // mes_referencia segue regra CCEE: MS+6du = 6du do mes seguinte ao mes
  // de referencia. Logo o mes_ref e o mes anterior ao mes da data.
  const ref = new Date(data.getFullYear(), data.getMonth() - 1, 1);
  const mes_referencia = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, "0")}`;
  return { data, mes_referencia, is_estimativa: true, fonte: "heuristica_6du" };
}

function fetchProximo6du(hoje?: string): Promise<Proximo6duInfo> {
  return getProximo6du(hoje)
    .then((r) => ({
      data: new Date(r.data + "T00:00:00"),
      mes_referencia: r.mes_referencia,
      is_estimativa: r.is_estimativa,
      fonte: r.fonte,
    }))
    .catch(() => heuristicaInfo(hoje ? new Date(hoje + "T00:00:00") : new Date()));
}

/** Invalida o cache (útil em testes ou após importer rodar). */
export function invalidateProximo6duCache(): void {
  cache.clear();
}

export interface UseProximo6duState {
  info: Proximo6duInfo | null;
  loading: boolean;
}

export function useProximo6du(hoje?: string): UseProximo6duState {
  const [state, setState] = useState<UseProximo6duState>({ info: null, loading: true });

  useEffect(() => {
    let cancel = false;
    const key = hoje ?? "_today_";
    const now = Date.now();
    let entry = cache.get(key);
    if (!entry || entry.expiresAt < now) {
      entry = {
        expiresAt: now + TTL_MS,
        promise: fetchProximo6du(hoje),
      };
      cache.set(key, entry);
    }
    entry.promise.then((info) => {
      if (cancel) return;
      setState({ info, loading: false });
    });
    return () => {
      cancel = true;
    };
  }, [hoje]);

  return state;
}
